/**
 * A single large figure with a label — the dashboard's basic unit.
 *
 * The figure is 48px Gilroy ExtraBold in green-dark with tabular figures. This
 * is the clearest case of §4.2's rule: the number *is* the data, so it gets the
 * green; the label above it does not.
 *
 * Rendered as a `<dl>` pair because that is what it is — a term and its value.
 * A `<div>` with two spans would look identical and tell a screen reader
 * nothing about which text labels which.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.4, §4.2
 */
import { cn } from '../../lib/cn';

export interface StatProps {
  /** The label. Uppercase treatment is applied here, not by the caller. */
  label: React.ReactNode;
  /**
   * The figure. A string rather than a number so the caller formats it through
   * `lib/format` — PRD §9.5 pins thousands separators and precision, and a raw
   * number here would bypass both.
   */
  value: string;
  /** A unit shown at two-thirds size beside the figure, e.g. `MW`. */
  unit?: string | undefined;
  /** A clarifying line beneath, e.g. what period the figure covers. */
  hint?: React.ReactNode;
  className?: string;
}

export function Stat({
  label,
  value,
  unit,
  hint,
  className,
}: StatProps): React.JSX.Element {
  return (
    <dl className={cn('p-s3', className)}>
      <dt className="text-body font-display mb-2.5 text-[10px] font-extrabold tracking-[0.12em] uppercase">
        {label}
      </dt>
      <dd className="text-green-dark font-display text-5xl leading-none font-extrabold tracking-[-0.02em] tabular-nums">
        {value}
        {unit !== undefined && <span className="ml-0.5 text-2xl">{unit}</span>}
      </dd>
      {hint !== undefined && (
        <dd className="text-body mt-2.5 text-xs font-light">{hint}</dd>
      )}
    </dl>
  );
}
